"use client";

import { useState } from "react";
import { Button } from "@/shadcn-components/ui/button";
import { cn } from "@/lib/utils";
import { useIndicatorLoggerStore } from "@/lib/custom-indicators/runtime/logger-store";

type TerminalTab = "terminal" | "output" | "logger";

const TABS: { id: TerminalTab; label: string }[] = [
  { id: "terminal", label: "Terminal" },
  { id: "output", label: "Output" },
  { id: "logger", label: "Logger" },
];

export default function CustomIndicatorEditorTerminal() {
  const [activeTab, setActiveTab] = useState<TerminalTab>("output");
  const buildLines = useIndicatorLoggerStore((s) => s.buildLines);
  const loggerLines = useIndicatorLoggerStore((s) => s.loggerLines);
  const terminalLines = useIndicatorLoggerStore((s) => s.terminalLines);
  const runtimeMessage = useIndicatorLoggerStore((s) => s.runtimeMessage);
  const runtimeReady = useIndicatorLoggerStore((s) => s.runtimeReady);
  const clearBuild = useIndicatorLoggerStore((s) => s.clearBuild);
  const clearLogger = useIndicatorLoggerStore((s) => s.clearLogger);
  const clearTerminal = useIndicatorLoggerStore((s) => s.clearTerminal);

  const runtimeFailed =
    !runtimeReady &&
    (runtimeMessage?.startsWith("Runtime failed:") ||
      buildLines.some((l) => l.startsWith("[Runtime]")));

  const outputText =
    buildLines.length > 0
      ? buildLines.join("\n")
      : runtimeMessage ??
        (runtimeReady
          ? "Ready. Click Compile to build the C# project."
          : runtimeFailed
            ? 'Run "npm run build:indicator-runtime" from apps/web (requires .NET 10 SDK), then restart the dev server.'
            : "Preparing .NET compiler runtime…");

  const terminalText =
    terminalLines.length > 0
      ? terminalLines.join("\n") + "\nvybe@indicator:~$ _"
      : "vybe@indicator:~$ _";

  const loggerText =
    loggerLines.length > 0
      ? loggerLines.join("\n")
      : "Chart logger output appears here (Logger.Print from your indicator).";

  const body =
    activeTab === "terminal"
      ? terminalText
      : activeTab === "output"
        ? outputText
        : loggerText;

  const handleClear = () => {
    if (activeTab === "terminal") clearTerminal();
    else if (activeTab === "output") clearBuild();
    else clearLogger();
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="flex h-8 shrink-0 items-center justify-between border-b border-border px-2">
        <div className="flex items-center gap-0.5">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "rounded px-2 py-1 text-[10px] font-medium transition-colors",
                activeTab === tab.id
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[10px]"
            onClick={handleClear}
          >
            Clear
          </Button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        <pre className="whitespace-pre-wrap px-3 py-2 font-mono text-xs text-muted-foreground">
          {body}
        </pre>
      </div>
    </div>
  );
}
